import { useUpdateOpportunityTask } from '@opportunities/-hooks/useUpdateOpportunityTask'
import { getTaskFieldErrors, taskSchema } from '@opportunities/-lib/taskValidation'
import { useCreateOpportunityTask } from '@opportunities/$opportunityId/-hooks/useCreateOpportunityTask'
import type { CreateOpportunityTaskRequest, OpportunityTask, UpdateOpportunityTaskRequest } from 'backend/types'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'
import { OPPORTUNITY_TASK_STATUS_IDS } from 'shared'
import { toast } from 'sonner'
import { getVisibleErrors, touchAll } from '@/lib/form-validation'
import { useTaskStatuses } from '@/shared-hooks/useTaskStatuses'

interface UseTaskSaveDialogParams {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunityId: number
  salesforceProjectId?: number | null
  task?: OpportunityTask | null
}

export function useTaskSaveDialog({
  open,
  onOpenChange,
  opportunityId,
  salesforceProjectId,
  task,
}: UseTaskSaveDialogParams) {
  const isEdit = !!task?.id

  // 編集時はtaskからsalesforceProjectIdを取得、新規作成時はpropsから取得
  const effectiveSalesforceProjectId = isEdit ? (task?.salesforce_project_id ?? null) : (salesforceProjectId ?? null)

  // フォーム状態
  const [title, setTitle] = useState(task?.title || '')
  const [description, setDescription] = useState(task?.description || '')
  const [statusId, setStatusId] = useState<number | null>(
    task?.opportunity_task_status_id || OPPORTUNITY_TASK_STATUS_IDS.OPEN,
  )
  const [expiredOn, setExpiredOn] = useState(
    task?.expired_on ? DateTime.fromISO(task.expired_on).toFormat('yyyy-MM-dd') : '',
  )
  const [memberId, setMemberId] = useState<number | null>(task?.member_id || null)
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  // ダイアログが閉じたときに状態をリセット
  useEffect(() => {
    if (open) {
      setTitle(task?.title || '')
      setDescription(task?.description || '')
      setStatusId(task?.opportunity_task_status_id || OPPORTUNITY_TASK_STATUS_IDS.OPEN)
      setExpiredOn(task?.expired_on ? DateTime.fromISO(task.expired_on).toFormat('yyyy-MM-dd') : '')
      setMemberId(task?.member_id || null)
      setTouched({})
    }
  }, [open, task])

  // データ取得
  const { data: statusesData } = useTaskStatuses()
  const statuses = statusesData?.statuses ?? []

  // ダイアログを閉じる
  const handleClose = () => {
    onOpenChange(false)
  }

  // ミューテーション
  const { mutate: createTask, isPending: isCreating } = useCreateOpportunityTask(opportunityId, {
    onSuccess: () => {
      toast.success('タスクを作成しました')
      handleClose()
    },
    onError: (error) => {
      toast.error(`タスク作成に失敗しました: ${error.message}`)
    },
  })

  const { mutate: updateTask, isPending: isUpdating } = useUpdateOpportunityTask({
    onSuccess: () => {
      toast.success('タスクを更新しました')
      handleClose()
    },
    onError: (error) => {
      toast.error(`タスク更新に失敗しました: ${error.message}`)
    },
  })

  const isPending = isCreating || isUpdating

  // バリデーション
  const allErrors = useMemo(
    () => getTaskFieldErrors({ title, description, statusId, memberId }),
    [title, description, statusId, memberId],
  )

  const errors = useMemo(() => getVisibleErrors(allErrors, touched), [allErrors, touched])

  // エラーがあるかどうか（表示中のエラーベースでチェック）
  const hasErrors = Object.keys(errors).length > 0

  // フィールド変更時にtouchedを更新
  const handleChange = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
  }

  // 保存
  const handleSave = () => {
    if (Object.keys(allErrors).length > 0) {
      setTouched(touchAll(taskSchema))
      toast.error('入力内容を確認してください')
      return
    }
    if (statusId === null || memberId === null) return

    if (isEdit && task) {
      const data: UpdateOpportunityTaskRequest = {
        task: title.trim(),
        description: description.trim(),
        opportunityTaskStatusId: statusId,
        expiredOn: expiredOn || null,
        memberId: memberId,
      }
      updateTask({ opportunityId, taskId: task.id, data })
    } else {
      const data: CreateOpportunityTaskRequest = {
        task: title.trim(),
        description: description.trim(),
        opportunityTaskStatusId: statusId,
        expiredOn: expiredOn || null,
        memberId: memberId,
      }
      createTask(data)
    }
  }

  return {
    // 状態
    isEdit,
    effectiveSalesforceProjectId,
    title,
    setTitle,
    description,
    setDescription,
    statusId,
    setStatusId,
    expiredOn,
    setExpiredOn,
    memberId,
    setMemberId,
    statuses,
    errors,
    hasErrors,
    isPending,

    // ハンドラ
    handleChange,
    handleSave,
    handleClose,
  }
}
